import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faHeadset,
  faArrowRight,
  faWaveSquare,
  faTrophy,
  faChartSimple,
  faPen,
  faTriangleExclamation,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';

import { useSelectedBot } from '../contexts/SelectedBotContext.js';
import FirstBotPrompt from '../components/FirstBotPrompt.js';
import { STUDIO_PATH, saveHomeView } from '../components/agentStudio/homeView';
import SuccessScoreModal, {
  calculateSuccessScore,
} from '../components/SuccessScoreModal.js';
import AgentAvailability from '../components/AgentAvailability.js';
import ChatModal from '../components/chatModal.js';
import useUpgrade from '../hooks/useUpgrade.js';
import {
  ActivityChart, RangeSwitch, Ring, SCORE_TARGETS, useActivityRange, useActivityStats, useOverviewData,
} from './overviewCards';
import './Overview.css';

const Overview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedBot, selectedBotId, bots, loading, openAddBot, refreshBots } =
    useSelectedBot();
  const { handleUpgradeClick, UpgradeModal } = useUpgrade({ onComplete: refreshBots });
  const { richBot, activityMap, loadRich } = useOverviewData(selectedBotId);
  const { rangeDays, changeRange, domainStart } = useActivityRange();
  const { series: activitySeries, total: activityTotal, yMax: activityYMax } =
    useActivityStats(activityMap, rangeDays);

  const [scoreOpen, setScoreOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [nameInput, setNameInput] = useState('');
  // Live AI-response count, fed by the embedded chat playground via its
  // onMessageCountChange callback. null until the chat reports — then it
  // overrides the server value so the "AI responses" tile ticks up live.
  const [liveCount, setLiveCount] = useState(null);
  const availRef = useRef(null);

  // Reset the live count when the active bot changes (the embedded chat is
  // remounted per bot via its key, so it re-reports from the server value).
  useEffect(() => {
    setLiveCount(null);
  }, [selectedBotId]);

  const bot = richBot || selectedBot;
  const score = useMemo(
    () => (richBot ? calculateSuccessScore(richBot, richBot.success_score ?? {}) : null),
    [richBot]
  );

  // Website-knowledge freshness nudge. Deliberately NOT part of the success
  // score: that score is an onboarding checklist of one-time steps that only
  // ever goes up, and a value that decays with time would read as a penalty for
  // doing nothing wrong. It lives here as its own prompt instead, and only once
  // the knowledge is actually old.
  //
  // No need to check whether automatic rescraping is on: a monthly schedule
  // re-stamps created_at on every run, so such a bot never reaches the
  // threshold in the first place.
  const knowledgeStaleDays = useMemo(() => {
    const raw = richBot?.success_score?.knowledgeLastRefreshed;
    const linkCount = richBot?.success_score?.websiteLinkCount ?? 0;
    if (!raw || linkCount === 0) return null;
    const ts = new Date(raw).getTime();
    if (Number.isNaN(ts)) return null;
    const days = Math.floor((Date.now() - ts) / 86400000);
    return days >= 45 ? days : null;
  }, [richBot]);

  if (loading) {
    return (
      <div className="ov-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
      </div>
    );
  }
  if (bots.length === 0) {
    return <FirstBotPrompt onAddBot={openAddBot} />;
  }
  if (!bot) {
    return (
      <div className="ov-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
      </div>
    );
  }

  const planName = bot.subscription_plan?.name || 'Free';
  const isPaid = planName && planName !== 'Free';
  const used = Number(bot.current_message_count ?? 0);
  const limit = Number(bot.subscription_plan?.message_limit ?? 0);
  // Show whichever is higher: the embedded chat's optimistic live count (so
  // chatting ticks the figure up instantly) or the server value (which a
  // background refresh bumps after the test-chat reports new usage). The two
  // converge; taking the max keeps the figure from snapping backwards and
  // stops a stale liveCount from masking a fresh server count.
  const displayedUsed = Math.max(liveCount ?? 0, used);
  const usagePct = limit > 0 ? Math.min(100, Math.round((displayedUsed / limit) * 100)) : 0;
  const atLimit = limit > 0 && displayedUsed >= limit;
  const billingLabel = isPaid ? t('ov_manage_billing') : t('ov_upgrade_plan');

  const currentName = bot.name || bot.domain_name || '';
  const startRename = () => {
    setNameInput(currentName);
    setRenaming(true);
  };
  const commitRename = async () => {
    const trimmed = nameInput.trim();
    setRenaming(false);
    setNameInput('');
    if (!trimmed || trimmed === currentName) return;
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}bots/${bot.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed }),
      });
      refreshBots();
      loadRich();
    } catch (_) {
      /* ignore */
    }
  };

  const goSettings = (b, tab) => {
    setScoreOpen(false);
    navigate(SCORE_TARGETS[tab] || SCORE_TARGETS.skills);
  };

  return (
    <div className="ov-page">
      <header className="ov-top">
        {renaming ? (
          <input
            className="ov-rename-input"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') {
                setRenaming(false);
                setNameInput('');
              }
            }}
            autoFocus
            maxLength={50}
          />
        ) : (
          <>
            <h1 className="ov-top-title">{currentName}</h1>
            <button type="button" className="ov-rename-btn" onClick={startRename}>
              <FontAwesomeIcon icon={faPen} /> {t('ov_rename')}
            </button>
          </>
        )}
        {/* the classic overview is the owner's choice of start page; this makes AI Studio the start page again */}
        <button
          type="button"
          className="ov-studio-btn"
          title={t('agent_studio_studio_view_hint')}
          onClick={() => {
            saveHomeView('studio');
            navigate(STUDIO_PATH);
          }}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} /> {t('agent_studio_studio_view_back')}
        </button>
      </header>

      <div className="ov-split">
        {/* Left — live test playground (the full chat component, inline) */}
        <div className="ov-split-left">
          <ChatModal
            key={selectedBotId}
            embedded
            open
            onClose={() => {}}
            bot={bot}
            currentMessageCount={used}
            messagePlanLimit={limit || 999999}
            planName={planName}
            openSubscriptionModalFromChat={handleUpgradeClick}
            onMessageCountChange={setLiveCount}
          />
        </div>

        {/* Right — live chat, usage, success score, activity */}
        <div className="ov-split-right">

        {/* Knowledge freshness nudge — only when the website knowledge is
            genuinely old. Deep-links straight into the refresh dialog. */}
        {knowledgeStaleDays != null && (
          <div
            className="ov-card ov-action ov-action-clickable ov-action-stale"
            role="button"
            tabIndex={0}
            onClick={() => navigate('/knowledge/website-links?action=check-updates')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/knowledge/website-links?action=check-updates');
              }
            }}
          >
            <span className="ov-action-ic">
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </span>
            <span className="ov-action-body">
              <span className="ov-action-title">{t('overview_knowledge_stale_title')}</span>
              <span className="ov-action-sub">
                <Trans
                  i18nKey="overview_knowledge_stale_text"
                  count={knowledgeStaleDays}
                  components={{ strong: <strong /> }}
                />
              </span>
            </span>
            <span className="ov-action-side">
              <FontAwesomeIcon icon={faArrowRight} />
            </span>
          </div>
        )}

        {/* Live chat — whole card opens the team-availability modal,
            except the toggle/chip controls (which keep their own clicks). */}
        <div
          className="ov-card ov-action ov-action-clickable"
          role="button"
          tabIndex={0}
          onClick={() => availRef.current?.openRoster()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              availRef.current?.openRoster();
            }
          }}
        >
          <span className="ov-action-ic">
            <FontAwesomeIcon icon={faHeadset} />
          </span>
          <span className="ov-action-body">
            <span className="ov-action-title">{t('ov_live_chat_title')}</span>
            <span className="ov-action-sub">{t('ov_live_chat_sub')}</span>
          </span>
          <span className="ov-action-side" onClick={(e) => e.stopPropagation()}>
            <AgentAvailability ref={availRef} bot={bot} />
          </span>
        </div>

        {/* Usage */}
        <div className="ov-card ov-tile">
          <div className="ov-tile-head">
            <span className="ov-tile-ic">
              <FontAwesomeIcon icon={faWaveSquare} />
            </span>
            <div className="ov-tile-titles">
              <span className="ov-tile-title">{t('ov_usage_title')}</span>
              <span className="ov-tile-sub">{t('ov_usage_sub')}</span>
            </div>
            <span className="ov-tile-plan">
              <span className="ov-tile-plan-label">{t('ov_current_plan')}</span>
              <span className="ov-tile-plan-name">{planName}</span>
            </span>
          </div>
          <div className="ov-usage-block">
            <div className="ov-usage-row">
              <div className="ov-usage-value">
                {displayedUsed.toLocaleString('de-CH')}
                {limit > 0 && (
                  <span className="ov-usage-sub"> / {limit.toLocaleString('de-CH')}</span>
                )}
              </div>
              {limit > 0 && (
                <span className="ov-usage-pct">
                  {atLimit ? t('ov_limit_reached') : t('ov_plan_pct_used', { pct: usagePct })}
                </span>
              )}
            </div>
            {limit > 0 && (
              <div className="ov-usage-track">
                <div
                  className={`ov-usage-fill${atLimit ? ' full' : ''}`}
                  style={{ width: `${usagePct}%` }}
                />
              </div>
            )}
          </div>
          <button
            className="ov-btn ov-btn-soft ov-tile-cta-soft"
            onClick={() => handleUpgradeClick(bot)}
          >
            {billingLabel} <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>

        {/* 4 · Success score */}
        <button
          type="button"
          className="ov-card ov-tile ov-tile-score"
          onClick={() => richBot && setScoreOpen(true)}
        >
          <div className="ov-tile-head">
            <span className="ov-tile-ic">
              <FontAwesomeIcon icon={faTrophy} />
            </span>
            <div className="ov-tile-titles">
              <span className="ov-tile-title">{t('ov_success_score_title')}</span>
              <span className="ov-tile-sub">{t('ov_success_score_sub')}</span>
            </div>
            {score && (
              <span className="ov-tile-tag">
                {score.completed}/{score.total}
              </span>
            )}
          </div>
          <div className="ov-score-body">
            {score ? (
              <Ring percentage={score.percentage} />
            ) : (
              <div className="ov-score-loading">
                <FontAwesomeIcon icon={faSpinner} spin />
              </div>
            )}
            <span className="ov-score-cta">
              {t('ov_see_what_to_improve')} <FontAwesomeIcon icon={faArrowRight} />
            </span>
          </div>
        </button>

        {/* ── Activity ─────────────────────────────────────────── */}
        <section className="ov-card ov-activity">
        <div className="ov-activity-head">
          <div className="ov-activity-title-group">
            <span className="ov-tile-ic">
              <FontAwesomeIcon icon={faChartSimple} />
            </span>
            <div>
              <h2 className="ov-section-title">{t('activity')}</h2>
              <p className="ov-section-sub">
                {t('ov_ai_responses_last_days', { count: rangeDays })}
              </p>
            </div>
          </div>
          <div className="ov-activity-head-right">
            <RangeSwitch rangeDays={rangeDays} onChange={changeRange} />
            <div className="ov-activity-total">
              <span className="ov-activity-total-num">
                {activityTotal.toLocaleString('de-CH')}
              </span>
              <span className="ov-activity-total-lbl">{t('ov_responses', { count: activityTotal })}</span>
            </div>
          </div>
        </div>
        <div className="ov-chart">
          <ActivityChart activityMap={activityMap} series={activitySeries} domainStart={domainStart}
            yMax={activityYMax} />
        </div>
        <div className="ov-activity-foot">
          <button className="ov-explore" onClick={() => navigate('/activity/insights')}>
            {t('ov_explore_more')} <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>
        </section>
        </div>
      </div>

      {scoreOpen && richBot && (
        <SuccessScoreModal
          open={scoreOpen}
          onClose={() => setScoreOpen(false)}
          bot={richBot}
          successData={richBot.success_score || {}}
          onSettingsClick={goSettings}
          onUpgradeClick={handleUpgradeClick}
        />
      )}
      {UpgradeModal}
    </div>
  );
};

export default Overview;
